import ky, { HTTPError, Options, ResponsePromise } from "ky";

import { THttpService } from "../types/httpService";
import { apiURL } from "../utils";
import { AUTO_LOGOUT_401_EXCLUDED_PATHS } from "../utils/authRouteConstants";
import { performAuthFailureLogout } from "../utils/authUtils";
import { tokenStorage } from "../utils/tokenStorage";
import { attemptSilentRefresh } from "../utils/tokenRefresh";

// Helper to check if a URL is an excluded path for 401 auto-logout handling
const isExcludedAuthPath = (url: string): boolean => {
  try {
    const pathname = new URL(url).pathname;
    // Only skip auto-logout for specifically allowlisted public endpoints
    if (AUTO_LOGOUT_401_EXCLUDED_PATHS.some((path) => pathname.includes(path))) {
      return true;
    }
    return false;
  } catch {
    // Fallback to simple string check if URL parsing fails
    if (AUTO_LOGOUT_401_EXCLUDED_PATHS.some((path) => url.includes(path))) {
      return true;
    }
    return false;
  }
};

export type TApiServiceOptions = Options;

// expect Server to respond with a JSON object with a message property on bad request
// READ-MORE: https://github.com/sindresorhus/ky?tab=readme-ov-file#httperror
export type TApiServiceError<
  T = {
    message?: string;
  },
> = HTTPError<T>;

export class ApiService implements THttpService<Options> {
  public options: Options;
  private kyInstance: ReturnType<(typeof ky)["create"]>;

  constructor(options: Options = {}) {
    this.options = options;

    // Base hooks that must always be present
    const baseBeforeRequest = [
      (request: Request) => {
        // Only set Content-Type to JSON if it's not already set and body is not URLSearchParams or FormData
        // ky automatically sets the correct Content-Type for URLSearchParams and FormData
        if (!request.headers.has("Content-Type")) {
          request.headers.set("Content-Type", "application/json");
        }
        request.headers.set("Accept-Language", "en-US");
      },
    ];

    // Guard against multiple simultaneous logout attempts when several requests 401 at once
    let isHandlingAuthFailure = false;

    const baseAfterResponse = [
      async (request: Request, options: Options, response: Response) => {
        if (response.status !== 401) return response;
        if (isExcludedAuthPath(request.url)) return response;

        const refreshed = await attemptSilentRefresh();

        if (refreshed) {
          const newToken = tokenStorage.getAccessToken();
          // Retry the original request once with the fresh token.
          // Strip the API base prefix so ky's prefixUrl applies correctly.
          const apiBase = apiURL().toString().replace(/\/$/, "") + "/";
          const path = request.url.startsWith(apiBase)
            ? request.url.slice(apiBase.length)
            : request.url;

          const { hooks: _h, headers: _originalHeaders, ...restOpts } = options;
          const method = request.method.toLowerCase() as "get" | "post" | "put" | "patch" | "delete";

          // afterResponse is intentionally cleared on the retry to prevent an
          // infinite loop if the server returns 401 again (e.g. new token rejected).
          return (this.kyInstance[method] as (u: string, o?: Options) => ResponsePromise)(path, {
            ...restOpts,
            headers: {
              Authorization: `Bearer ${newToken ?? ""}`,
              "Content-Type": "application/json",
              "Accept-Language": "en-US",
            },
            hooks: { afterResponse: [] },
          });
        }

        // Refresh failed — clear cookies and redirect to login
        if (!isHandlingAuthFailure) {
          isHandlingAuthFailure = true;
          performAuthFailureLogout();
        }
        return response;
      },
    ];

    // Merge hooks from options with base hooks
    // This ensures the 401 interceptor is always present even when services provide custom hooks
    const mergedHooks = {
      beforeRequest: [...baseBeforeRequest, ...(options.hooks?.beforeRequest ?? [])],
      afterResponse: [...baseAfterResponse, ...(options.hooks?.afterResponse ?? [])],
      // Include any other hook types from options
      ...(options.hooks?.beforeError ? { beforeError: options.hooks.beforeError } : {}),
      ...(options.hooks?.beforeRetry ? { beforeRetry: options.hooks.beforeRetry } : {}),
    };

    // Extract hooks from options to avoid passing them twice
    const { hooks: _hooks, ...restOptions } = options;

    // Default to 30 s; override per-call via options.timeout if needed.
    const defaultTimeout = Number(import.meta.env.VITE_API_TIMEOUT_MS) || 30_000;

    this.kyInstance = ky.create({
      hooks: mergedHooks,
      prefixUrl: apiURL().toString(),
      timeout: defaultTimeout,
      retry: {
        limit: 0,
      },
      ...restOptions,
    });
  }

  public extend(options: Options) {
    this.kyInstance = this.kyInstance.extend(options);
  }

  public async get<R = unknown>(url: string, options?: Options): Promise<R> {
    return this.kyInstance.get(url, options).json();
  }

  public getRaw(url: string, options?: Options): ResponsePromise {
    return this.kyInstance.get(url, options);
  }

  public async post<R = unknown, B = unknown>(url: string, body: B, options?: Options): Promise<R> {
    const requestOptions: Options = { ...options };

    if (body instanceof FormData || body instanceof URLSearchParams) {
      requestOptions.body = body;
    } else if (typeof body !== "undefined") {
      requestOptions.json = body;
    }

    return this.kyInstance.post(url, requestOptions).json();
  }

  public async put<R = unknown, B = unknown>(url: string, body?: B, options?: Options): Promise<R> {
    return this.kyInstance.put(url, { json: body, ...options }).json();
  }

  public async patch<R = unknown, B = unknown>(url: string, body?: B, options?: Options): Promise<R> {
    return this.kyInstance.patch(url, { json: body, ...options }).json();
  }

  public async delete<R = unknown, B = unknown>(url: string, body?: B, options?: Options): Promise<R> {
    return this.kyInstance.delete(url, { json: body, ...options }).json();
  }
}
